/**
 * 保存に失敗したときに画面へ出すエラー文。
 *
 * サーバーは項目ごとのエラー（{ plate_number: ['…'] }）か、{ detail: '…' } を返す。
 * 利用者に見せる文だけを取り出し、項目名（英語の内部名）と code は出さない。
 * 応答が無い（通信できない）ときは fallback を出す。
 */
export function apiErrorMessages(error: unknown, fallback: string): string[] {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') {
    return typeof data === 'string' && data ? [data] : [fallback];
  }
  // code は機械向けの値（permission_denied など）なので表示しない。
  const messages = Object.entries(data as Record<string, unknown>)
    .filter(([key]) => key !== 'code')
    .flatMap(([, value]) => flatten(value));
  return messages.length > 0 ? messages : [fallback];
}

function flatten(value: unknown): string[] {
  if (typeof value === 'string') return value ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(flatten);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flatten);
  return [];
}

export interface ErrorLabels {
  /** 応答が無い（通信できない）。 */
  network: string;
  /** 403。権限が無い。 */
  forbidden: string;
  /** 5xx。サーバー側の障害。 */
  server: string;
  /** 上のどれでもなく、サーバーの説明も無いとき。 */
  fallback: string;
}

/**
 * ADM-15 / ADM-24。失敗の種類（通信断・403・5xx・入力の誤り）を区別して説明する。
 * 以前はどれも同じ「保存できませんでした」になり、利用者が次に何をすればよいか分からなかった。
 * 400 などの入力の誤りはサーバーの説明をそのまま出す。
 */
export function classifiedErrorMessages(error: unknown, labels: ErrorLabels): string[] {
  switch (errorKind(error)) {
    case 'network':
      return [labels.network];
    case 'forbidden':
      return [labels.forbidden];
    case 'server':
      // サーバーが理由を決めて返した 5xx（code と detail を持つ。招待メールを送れなかった
      // email_not_sent など）は、その説明を出す。HTML などの障害の応答は一般の説明にする。
      return applicationDetail(error) ?? [labels.server];
    case 'unknown':
      return [labels.fallback];
    default:
      return apiErrorMessages(error, labels.fallback);
  }
}

function applicationDetail(error: unknown): string[] | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const { code, detail } = data as Record<string, unknown>;
  return typeof code === 'string' && typeof detail === 'string' && detail ? [detail] : null;
}

/**
 * 失敗の種類。network: 応答が無い（通信断）、forbidden: 403、server: 5xx、
 * other: そのほかの応答（400 など、サーバーの説明がある）、unknown: 応答も要求も無い失敗。
 */
export type ErrorKind = 'network' | 'forbidden' | 'server' | 'other' | 'unknown';

export function errorKind(error: unknown): ErrorKind {
  const err = error as {
    response?: { status?: number };
    request?: unknown;
    code?: string;
    message?: string;
  };
  const response = err?.response;
  if (!response) {
    const isNetwork =
      err?.code === 'ERR_NETWORK' || err?.message === 'Network Error' || Boolean(err?.request);
    return isNetwork ? 'network' : 'unknown';
  }
  if (response.status === 403) return 'forbidden';
  if (response.status !== undefined && response.status >= 500) return 'server';
  return 'other';
}

export interface DeviceAssignmentLabels {
  /** device_in_use。他の運転者が使用中。 */
  inUse: string;
  /** device_not_available。修理中・故障などで割り当てられない。 */
  notAvailable: string;
}

/**
 * ADM-14 / BE-02。運転者の登録・編集で機器を割り当てられない 400 の説明。
 *
 * サーバーは項目のエラー（devices）に加えて最上位に code（device_in_use /
 * device_not_available）を付ける（openapi-schema.yaml の DriverValidationError）。
 * code があれば devices の文（サーバーの言語）の代わりに画面の言語の理由を出し、
 * ほかの項目のエラーは従来どおり並べる。code が無ければ null（従来の表示に任せる）。
 */
export function deviceAssignmentMessages(
  error: unknown,
  labels: DeviceAssignmentLabels
): string[] | null {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  if (response?.status !== 400 || !data || typeof data !== 'object') return null;
  const { code } = data as Record<string, unknown>;
  const reason =
    code === 'device_in_use'
      ? labels.inUse
      : code === 'device_not_available'
      ? labels.notAvailable
      : null;
  if (!reason) return null;
  const others = Object.entries(data as Record<string, unknown>)
    .filter(([key]) => key !== 'code' && key !== 'devices')
    .flatMap(([, value]) => flatten(value));
  return [reason, ...others];
}
